import {
  IFormulaArgumentSelectorName,
  TFieldType,
  TFormulaArgumentType
} from 'volta/models/types/customFieldDefinition';
import { parseNumber } from 'volta/utils/math-utils';

import { helper } from '@ember/component/helper';

const Operators = {
  multiply: (args: number[] = []) =>
    args.reduce((m, n) => {
      return (parseNumber(m) ?? 0) * (parseNumber(n) ?? 0);
    }),
  add: (args: number[] = []) =>
    args.reduce((m, n) => (parseNumber(m) ?? 0) + (parseNumber(n) ?? 0)),
  subtract: (args: number[] = []) =>
    args.reduce((m, n) => (parseNumber(m) ?? 0) - (parseNumber(n) ?? 0)),
  if: ([check, left, right]: [boolean, unknown, unknown]) => (Boolean(check) ? left : right),
  equal: ([left, right]: [unknown, unknown]) => left === right,
  notEqual: ([left, right]: [unknown, unknown]) => left !== right
};

const Functions = {
  lower: ([arg]: [string]) => (arg ? `${arg}`.toLowerCase() : undefined),
  upper: ([arg]: [string]) => (arg ? `${arg}`.toUpperCase() : undefined),
  join: (args = []) => {
    const words = args.filter(Boolean);
    return words.length ? words.join(', ') : undefined;
  },
  now: () => new Date()
};

interface IArg {
  id: string;
  name: IFormulaArgumentSelectorName;
  resultType: TFieldType;
  type: TFormulaArgumentType;
  value: string | number | Date;
  formula: IFormula;
}

interface IFormula extends IArg {
  args: IFormula[];
}

type TFormulaReturnValue = string | number | Date | IFormula;

type FFormula<T> = (values: { [key: string]: T }, formula: IFormula) => T;
type TFormulas<T> = Record<TFormulaArgumentType, FFormula<T>>;

const Formulas: TFormulas<TFormulaReturnValue> = {
  PROPERTY: (values, formula) => formula.id && values[formula.id],
  CONSTANT: (_, formula) => formula.value,
  FUNCTION: (values, formula) =>
    // @ts-ignore - Circular operation
    Functions[formula.name](
      formula.args?.map((arg): IFormula => {
        return Formulas[arg.type](values, arg) as IFormula;
      })
    ),
  OPERATOR: (values, formula) =>
    // @ts-ignore - Circular operation
    Operators[formula.name](
      formula.args.map((arg): IFormula => {
        return arg.type
          ? (Formulas[arg.type](values, arg) as IFormula)
          : (Formulas[arg.formula.type](values, arg.formula) as IFormula);
      })
    )
};

/**
 * Function for calculating a formula
 * @param {{[string]: any}} param0 Values
 * @param {{id: string, name: string, resultType: string, type: string, args: {}}}
 */
export function customFieldFormula(
  [values, formula]: [{ [key: string]: TFormulaReturnValue }, IFormula] /*, hash*/
) {
  return Formulas[formula.type]?.(values, formula);
}

export default helper(customFieldFormula);
